import { Injectable } from '@angular/core';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';

/** Fila estándar de planilla Transaction_Entry ASINET. */
export interface AsinetRow {
  AccountCode: string;
  SubAccountCode: string;
  FundCode: number | string;
  FunctionCode: string;
  RestrictionCode: string;
  EntityValue: number;
  SendMemo: string;
  Description: string;
  DueDate?: string;
  Memo?: string;
  InvoiceNumber?: string;
  InvoiceDate?: string;
  CurrencyAmount?: string | number;
}

export interface AsinetDownloadOptions {
  /** Nombre de la hoja Excel. */
  sheetName?: string;
  rows: AsinetRow[];
  /** Nombre de archivo sin extensión. */
  fileName: string;
}

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const ASINET_COLUMNS = [
  { header: 'AccountCode', key: 'AccountCode', width: 15 },
  { header: 'SubAccountCode', key: 'SubAccountCode', width: 15 },
  { header: 'FundCode', key: 'FundCode', width: 10 },
  { header: 'FunctionCode', key: 'FunctionCode', width: 15 },
  { header: 'RestrictionCode', key: 'RestrictionCode', width: 15 },
  { header: 'EntityValue', key: 'EntityValue', width: 15 },
  { header: 'SendMemo', key: 'SendMemo', width: 12 },
  { header: 'Description', key: 'Description', width: 40 },
  { header: 'DueDate', key: 'DueDate', width: 15 },
  { header: 'Memo', key: 'Memo', width: 40 },
  { header: 'InvoiceNumber', key: 'InvoiceNumber', width: 15 },
  { header: 'InvoiceDate', key: 'InvoiceDate', width: 15 },
  { header: 'CurrencyAmount', key: 'CurrencyAmount', width: 15 },
];

@Injectable({ providedIn: 'root' })
export class AsinetExportService {
  readonly months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ] as const;

  /** Genera y descarga la planilla ASINET con columnas fijas. */
  download(options: AsinetDownloadOptions): void {
    const sheetName = options.sheetName || 'Transaction_Entry';
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(sheetName);
    worksheet.columns = ASINET_COLUMNS as any;
    (options.rows || []).forEach((row) => worksheet.addRow(this.normalizeRow(row)));
    workbook.xlsx.writeBuffer().then((buffer) => {
      this.saveExcelFile(buffer, options.fileName);
    });
  }

  /** Completa campos opcionales vacíos de una fila ASINET. */
  normalizeRow(row: AsinetRow): AsinetRow {
    return {
      AccountCode: String(row.AccountCode ?? ''),
      SubAccountCode: String(row.SubAccountCode ?? ''),
      FundCode: row.FundCode ?? '',
      FunctionCode: String(row.FunctionCode ?? ''),
      RestrictionCode: String(row.RestrictionCode ?? ''),
      EntityValue: Number(row.EntityValue) || 0,
      SendMemo: row.SendMemo ?? 'False',
      Description: String(row.Description ?? ''),
      DueDate: row.DueDate ?? '',
      Memo: row.Memo ?? '',
      InvoiceNumber: row.InvoiceNumber ?? '',
      InvoiceDate: row.InvoiceDate ?? '',
      CurrencyAmount: row.CurrencyAmount ?? ''
    };
  }

  /** Fecha DD.MM.YYYY para glosas ASINET. */
  formatDateDot(dateValue: unknown): string {
    if (!dateValue) return 'N/A';
    const raw = String(dateValue).slice(0, 10);
    const [y, m, d] = raw.split('-');
    if (y && m && d && y.length === 4) return `${d}.${m}.${y}`;
    const parsed = new Date(dateValue as any);
    if (isNaN(parsed.getTime())) return raw;
    return `${String(parsed.getDate()).padStart(2, '0')}.${String(parsed.getMonth() + 1).padStart(2, '0')}.${parsed.getFullYear()}`;
  }

  /** Stamp compacto para nombres de archivo. */
  dateStamp(): string {
    return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
  }

  /** Nombre de mes 1..12. */
  monthName(period: number | null | undefined): string {
    const idx = (Number(period) || 1) - 1;
    return this.months[idx] ?? '';
  }

  saveExcelFile(buffer: ArrayBuffer | Blob, fileName: string): void {
    const data = buffer instanceof Blob
      ? buffer
      : new Blob([buffer], { type: EXCEL_MIME });
    const name = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    saveAs(data, name);
  }
}
